import React, { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, GeoJSON, useMapEvents, useMap } from 'react-leaflet';
import type { Listing } from '../types/index.js';
import type { BebauungsplanListItem } from '../types/bebauungsplan';
import { GEBIETSART_LABELS } from '../types/bebauungsplan';
import L from 'leaflet';

// SVG-based colored marker icons
function createMarkerIcon(fill: string, stroke: string, size: number) {
  const scale = size / 25;
  const w = Math.round(25 * scale);
  const h = Math.round(41 * scale);
  return L.divIcon({
    className: '',
    iconSize: [w, h],
    iconAnchor: [w / 2, h],
    popupAnchor: [0, -h],
    html: `<div class="marker-pin"><svg width="${w}" height="${h}" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg">
      <path d="M12.5 0C5.6 0 0 5.6 0 12.5C0 21.9 12.5 41 12.5 41S25 21.9 25 12.5C25 5.6 19.4 0 12.5 0Z" fill="${fill}" stroke="${stroke}" stroke-width="1.5"/>
      <circle cx="12.5" cy="12.5" r="5" fill="white" opacity="0.8"/>
    </svg></div>`,
  });
}

// Normal listing icons
const verifiedIcon = createMarkerIcon('#86efac', '#16a34a', 25);
const verifiedHoverIcon = createMarkerIcon('#86efac', '#16a34a', 33);
const unverifiedIcon = createMarkerIcon('#fdba74', '#ea580c', 25);
const unverifiedHoverIcon = createMarkerIcon('#fdba74', '#ea580c', 33);

// Selected listing icons — darker/more saturated color, slightly larger
const verifiedSelectedIcon = createMarkerIcon('#16a34a', '#14532d', 30);
const verifiedSelectedHoverIcon = createMarkerIcon('#16a34a', '#14532d', 36);
const unverifiedSelectedIcon = createMarkerIcon('#ea580c', '#9a3412', 30);
const unverifiedSelectedHoverIcon = createMarkerIcon('#ea580c', '#9a3412', 36);

L.Marker.prototype.options.icon = unverifiedIcon;

// Generic marker for non-listing entities
export interface MapMarker {
  id: number;
  latitude: number;
  longitude: number;
  label: string;
  sublabel?: string;
  color: 'green' | 'blue' | 'purple' | 'indigo' | 'teal' | 'orange';
  link: string;
  linkLabel?: string;
}

const markerColors: Record<MapMarker['color'], { fill: string; stroke: string }> = {
  green:  { fill: '#86efac', stroke: '#16a34a' },
  blue:   { fill: '#93c5fd', stroke: '#2563eb' },
  purple: { fill: '#c4b5fd', stroke: '#7c3aed' },
  indigo: { fill: '#a5b4fc', stroke: '#4338ca' },
  teal:   { fill: '#5eead4', stroke: '#0d9488' },
  orange: { fill: '#fdba74', stroke: '#ea580c' },
};

// Pre-create icons for each color
const genericIcons = Object.fromEntries(
  Object.entries(markerColors).map(([key, { fill, stroke }]) => [key, createMarkerIcon(fill, stroke, 25)])
) as Record<MapMarker['color'], L.DivIcon>;

// Selected generic marker icons — darker color, slightly larger
const selectedMarkerColors: Record<MapMarker['color'], { fill: string; stroke: string }> = {
  green:  { fill: '#16a34a', stroke: '#14532d' },
  blue:   { fill: '#2563eb', stroke: '#1e3a8a' },
  purple: { fill: '#7c3aed', stroke: '#4c1d95' },
  indigo: { fill: '#4338ca', stroke: '#1e1b4b' },
  teal:   { fill: '#0d9488', stroke: '#134e4a' },
  orange: { fill: '#ea580c', stroke: '#9a3412' },
};

const genericSelectedIcons = Object.fromEntries(
  Object.entries(selectedMarkerColors).map(([key, { fill, stroke }]) => [key, createMarkerIcon(fill, stroke, 30)])
) as Record<MapMarker['color'], L.DivIcon>;

interface MapProps {
  listings?: Listing[];
  markers?: MapMarker[];
  /** Bebauungsplan-Polygone für den optionalen B-Plan-Layer. Geometrie muss befüllt sein. */
  bebauungsplaene?: BebauungsplanListItem[];
  center?: [number, number];
  zoom?: number;
  hoveredListingId?: number | null;
  onHoverListing?: (id: number | null) => void;
  onMapMove?: (center: [number, number], zoom: number) => void;
  /** When set, auto-fit bounds to markers. Value change triggers re-fit. */
  fitBoundsKey?: string;
  /** When set, pre-selects this listing marker and flies to it. Skips fitBounds. */
  selectedListingId?: number | null;
  /** When set, pre-selects this generic marker (by id) and flies to it. Skips fitBounds. */
  focusedMarkerId?: number | null;
  /** When true, drag (with or without shift) draws a selection rectangle. */
  selectionMode?: boolean;
  /** IDs of listings currently in the bulk selection — drawn with a highlight ring. */
  selectedListingIds?: Set<number>;
  /** Called when the user finishes drawing a selection rectangle. */
  onSelectInBounds?: (bounds: L.LatLngBounds, additive: boolean) => void;
  /** Toggles the persistent selection mode. When provided, a toggle button is rendered. */
  onToggleSelectionMode?: () => void;
}

function MapEventHandler({ onMapMove, onMapClick }: {
  onMapMove?: (center: [number, number], zoom: number) => void;
  onMapClick?: () => void;
}) {
  useMapEvents({
    moveend: (e) => {
      const center = e.target.getCenter();
      const zoom = e.target.getZoom();
      onMapMove?.([center.lat, center.lng], zoom);
    },
    click: () => onMapClick?.(),
  });
  return null;
}

/** Automatically fits map bounds to a set of points when fitBoundsKey changes */
function FitBoundsController({ points, fitBoundsKey, fallbackCenter }: {
  points: [number, number][];
  fitBoundsKey?: string;
  fallbackCenter?: [number, number];
}) {
  const map = useMap();
  const lastKeyRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (fitBoundsKey === undefined || fitBoundsKey === lastKeyRef.current) return;
    lastKeyRef.current = fitBoundsKey;

    if (points.length > 0) {
      const bounds = L.latLngBounds(points.map(([lat, lng]) => L.latLng(lat, lng)));
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
    } else if (fallbackCenter) {
      map.setView(fallbackCenter, 12);
    }
  }, [fitBoundsKey, points, fallbackCenter, map]);

  return null;
}

/**
 * Lets the user draw a rectangle on the map to lasso-select markers.
 * Active when `selectionMode` is true (any drag) OR when shift is held (any time).
 * Disables Leaflet's built-in boxZoom so shift+drag does not zoom.
 */
function BoxSelectController({ selectionMode, onSelect }: {
  selectionMode: boolean;
  onSelect?: (bounds: L.LatLngBounds, additive: boolean) => void;
}) {
  const map = useMap();
  const startRef = useRef<L.LatLng | null>(null);
  const rectRef = useRef<L.Rectangle | null>(null);
  const additiveRef = useRef(false);
  const draggingRef = useRef(false);

  useEffect(() => {
    // boxZoom is the shift+drag zoom; we always disable it because shift+drag is now selection
    map.boxZoom.disable();
    return () => {
      map.boxZoom.enable();
    };
  }, [map]);

  useEffect(() => {
    const container = map.getContainer();

    const cleanupRect = () => {
      if (rectRef.current) {
        rectRef.current.remove();
        rectRef.current = null;
      }
    };

    const onMouseDown = (e: MouseEvent) => {
      // Only react to plain left-button drags; modifiers other than shift/ctrl/meta we ignore
      if (e.button !== 0) return;
      const useSelection = selectionMode || e.shiftKey;
      if (!useSelection) return;

      e.preventDefault();
      map.dragging.disable();
      draggingRef.current = true;
      additiveRef.current = e.ctrlKey || e.metaKey;
      startRef.current = map.mouseEventToLatLng(e);
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!draggingRef.current || !startRef.current) return;
      const current = map.mouseEventToLatLng(e);
      const bounds = L.latLngBounds(startRef.current, current);
      if (!rectRef.current) {
        rectRef.current = L.rectangle(bounds, {
          className: 'map-lasso-rect',
          interactive: false,
        }).addTo(map);
      } else {
        rectRef.current.setBounds(bounds);
      }
    };

    const onMouseUp = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      map.dragging.enable();
      const start = startRef.current;
      startRef.current = null;
      const finalBounds = rectRef.current?.getBounds() ?? null;
      cleanupRect();
      // Ignore accidental tiny drags (single-pixel jitter when clicking)
      if (start && finalBounds) {
        const sw = map.latLngToContainerPoint(finalBounds.getSouthWest());
        const ne = map.latLngToContainerPoint(finalBounds.getNorthEast());
        if (Math.abs(sw.x - ne.x) > 6 && Math.abs(sw.y - ne.y) > 6) {
          onSelect?.(finalBounds, additiveRef.current);
        }
      }
    };

    container.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      container.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      cleanupRect();
      map.dragging.enable();
    };
  }, [map, selectionMode, onSelect]);

  return null;
}

/** Flies to a position once — used to center on a pre-selected marker */
function FlyToController({ position }: { position: [number, number] | null }) {
  const map = useMap();
  const didFly = useRef(false);

  useEffect(() => {
    if (!position || didFly.current) return;
    didFly.current = true;
    map.setView(position, 16);
  }, [position, map]);

  return null;
}

const Map: React.FC<MapProps> = ({ listings, markers, bebauungsplaene, center, zoom = 11, hoveredListingId, onHoverListing, onMapMove, fitBoundsKey, selectedListingId, focusedMarkerId, selectionMode, selectedListingIds, onSelectInBounds, onToggleSelectionMode }) => {
  const [satellite, setSatellite] = useState(false);
  const [showBebauungsplaene, setShowBebauungsplaene] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(() => {
    if (selectedListingId) return `listing-${selectedListingId}`;
    if (focusedMarkerId != null) {
      const m = (markers || []).find(m => m.id === focusedMarkerId);
      if (m) return `marker-${m.color}-${m.id}`;
    }
    return null;
  });
  // deselectingKey holds the marker currently playing the scale-out animation.
  // It still renders with the selected (larger) icon so the animation has something to shrink.
  const [deselectingKey, setDeselectingKey] = useState<string | null>(null);
  const markerRefs = useRef<Record<string, L.Marker | null>>({});
  const deselectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSelect = useCallback((key: string) => {
    // Cancel any in-progress deselect animation
    if (deselectTimerRef.current) {
      clearTimeout(deselectTimerRef.current);
      deselectTimerRef.current = null;
      setDeselectingKey(null);
    }

    if (selectedKey === key) {
      // Toggle off: close popup, keep selected icon visible while animation plays, then swap
      markerRefs.current[key]?.closePopup();
      setSelectedKey(null);
      setDeselectingKey(key);
      deselectTimerRef.current = setTimeout(() => {
        setDeselectingKey(null);
        deselectTimerRef.current = null;
      }, 180);
    } else {
      // Switch to new marker: close old popup, animate old one out, select new one
      if (selectedKey) {
        markerRefs.current[selectedKey]?.closePopup();
        setDeselectingKey(selectedKey);
        deselectTimerRef.current = setTimeout(() => {
          setDeselectingKey(null);
          deselectTimerRef.current = null;
        }, 180);
      }
      setSelectedKey(key);
    }
  }, [selectedKey]);

  const handleDeselect = useCallback(() => {
    if (!selectedKey) return;
    if (deselectTimerRef.current) {
      clearTimeout(deselectTimerRef.current);
      deselectTimerRef.current = null;
      setDeselectingKey(null);
    }
    markerRefs.current[selectedKey]?.closePopup();
    setSelectedKey(null);
    setDeselectingKey(selectedKey);
    deselectTimerRef.current = setTimeout(() => {
      setDeselectingKey(null);
      deselectTimerRef.current = null;
    }, 180);
  }, [selectedKey]);

  // Pop-in animation for newly selected marker
  useEffect(() => {
    if (!selectedKey) return;
    const pin = markerRefs.current[selectedKey]?.getElement()?.querySelector<HTMLElement>('.marker-pin');
    if (!pin) return;
    pin.classList.remove('marker-pin-anim', 'marker-pin-desel');
    void pin.offsetHeight;
    pin.classList.add('marker-pin-anim');
  }, [selectedKey]);

  // Pop-out animation for deselecting marker (icon stays selected while this plays)
  useEffect(() => {
    if (!deselectingKey) return;
    const pin = markerRefs.current[deselectingKey]?.getElement()?.querySelector<HTMLElement>('.marker-pin');
    if (!pin) return;
    pin.classList.remove('marker-pin-anim', 'marker-pin-desel');
    void pin.offsetHeight;
    pin.classList.add('marker-pin-desel');
  }, [deselectingKey]);

  // Filter listings with valid coordinates
  const validListings = useMemo(() =>
    (listings || []).filter(
      l => l.latitude != null && l.longitude != null && l.latitude !== 0 && l.longitude !== 0
    ),
  [listings]);

  // Offset overlapping markers so all pins are visible
  const offsetListings = useMemo(() => validListings.map((listing, _i, arr) => {
    const key = `${listing.latitude!.toFixed(4)},${listing.longitude!.toFixed(4)}`;
    const sameSpot = arr.filter(
      l => `${l.latitude!.toFixed(4)},${l.longitude!.toFixed(4)}` === key
    );
    if (sameSpot.length <= 1) return listing;
    const idx = sameSpot.indexOf(listing);
    const angle = (2 * Math.PI * idx) / sameSpot.length;
    const offset = 0.0003; // ~30m spread
    return {
      ...listing,
      latitude: listing.latitude! + offset * Math.cos(angle),
      longitude: listing.longitude! + offset * Math.sin(angle),
    };
  }), [validListings]);

  const validMarkers = useMemo(() =>
    (markers || []).filter(
      m => m.latitude != null && m.longitude != null && m.latitude !== 0 && m.longitude !== 0
    ),
  [markers]);

  // Multi-selection highlight ring: toggle a CSS class on each affected marker's pin element.
  // Runs after offsetListings render so marker DOM elements exist.
  useEffect(() => {
    Object.entries(markerRefs.current).forEach(([key, marker]) => {
      if (!key.startsWith('listing-')) return;
      const pin = marker?.getElement()?.querySelector<HTMLElement>('.marker-pin');
      if (!pin) return;
      const id = Number(key.slice('listing-'.length));
      if (selectedListingIds?.has(id)) pin.classList.add('marker-pin-selected');
      else pin.classList.remove('marker-pin-selected');
    });
  }, [selectedListingIds, offsetListings]);

  // All points for bounds calculation
  const allPoints: [number, number][] = useMemo(() => [
    ...validListings.map(l => [l.latitude!, l.longitude!] as [number, number]),
    ...validMarkers.map(m => [m.latitude, m.longitude] as [number, number]),
  ], [validListings, validMarkers]);

  const mapCenter: [number, number] = center || (
    allPoints.length > 0
      ? [
          allPoints.reduce((sum, p) => sum + p[0], 0) / allPoints.length,
          allPoints.reduce((sum, p) => sum + p[1], 0) / allPoints.length,
        ]
      : [52.5200, 13.4050] // Default to Berlin
  );

  const flyToPosition = useMemo<[number, number] | null>(() => {
    if (selectedListingId) {
      const l = validListings.find(l => l.id === selectedListingId);
      return l ? [l.latitude!, l.longitude!] : null;
    }
    if (focusedMarkerId != null) {
      const m = validMarkers.find(m => m.id === focusedMarkerId);
      return m ? [m.latitude, m.longitude] : null;
    }
    return null;
  }, [selectedListingId, focusedMarkerId, validListings, validMarkers]);

  // Auto-open the popup of a pre-focused marker once on mount.
  // Subsequent clicks are handled by Leaflet's default popup behavior.
  const didAutoOpenRef = useRef(false);
  useEffect(() => {
    if (didAutoOpenRef.current) return;
    const initialKey =
      selectedListingId ? `listing-${selectedListingId}`
      : focusedMarkerId != null
        ? (() => {
            const m = validMarkers.find(m => m.id === focusedMarkerId);
            return m ? `marker-${m.color}-${m.id}` : null;
          })()
        : null;
    if (!initialKey) return;
    const marker = markerRefs.current[initialKey];
    if (!marker) return;
    didAutoOpenRef.current = true;
    const id = window.setTimeout(() => marker.openPopup(), 300);
    return () => window.clearTimeout(id);
  }, [selectedListingId, focusedMarkerId, validMarkers]);

  return (
    <div className="h-full w-full relative">
      <MapContainer
        center={mapCenter}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
        className="z-0"
      >
        {satellite ? (
          <TileLayer
            key="satellite"
            attribution="Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          />
        ) : (
          <TileLayer
            key="osm"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
        )}
        <MapEventHandler onMapMove={onMapMove} onMapClick={handleDeselect} />
        {onSelectInBounds && (
          <BoxSelectController selectionMode={!!selectionMode} onSelect={onSelectInBounds} />
        )}
        {(selectedListingId || focusedMarkerId != null)
          ? <FlyToController position={flyToPosition} />
          : <FitBoundsController points={allPoints} fitBoundsKey={fitBoundsKey} fallbackCenter={center} />
        }

        {/* Bebauungsplan-Polygone (Layer unter den Markern) */}
        {showBebauungsplaene && bebauungsplaene?.map(bp => {
          if (!bp.geometry_geojson) return null;
          const isWR = bp.gebietsart === 'WR';
          const style = isWR
            ? { color: '#dc2626', fillColor: '#fca5a5', fillOpacity: 0.35, weight: 2 }
            : { color: '#2563eb', fillColor: '#93c5fd', fillOpacity: 0.20, weight: 1 };
          return (
            <GeoJSON
              key={`bplan-${bp.id}`}
              data={bp.geometry_geojson}
              style={() => style}
            >
              <Popup className="modern-popup">
                <div className="w-[260px]">
                  <div className={`h-1 ${isWR ? 'bg-red-500' : 'bg-blue-500'}`} />
                  <div className="p-4">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 leading-none">
                        Bebauungsplan
                      </span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium leading-none ring-1 ${
                        isWR
                          ? 'bg-red-50 text-red-700 ring-red-200'
                          : 'bg-blue-50 text-blue-700 ring-blue-200'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${isWR ? 'bg-red-500' : 'bg-blue-500'}`} />
                        {GEBIETSART_LABELS[bp.gebietsart] ?? bp.gebietsart}
                      </span>
                    </div>
                    <h3 className="font-semibold text-sm text-gray-900 leading-snug mb-1">
                      {bp.name}
                    </h3>
                    {bp.plan_nr && (
                      <div className="text-xs text-gray-500 mb-2">Plan-Nr.: {bp.plan_nr}</div>
                    )}
                    {bp.rechtskraft_datum && (
                      <div className="text-xs text-gray-400 mb-3">
                        Rechtskräftig seit {bp.rechtskraft_datum}
                      </div>
                    )}
                    <div className="border-t border-gray-100 mb-3" />
                    <button
                      onClick={() => window.open(`/bebauungsplaene/${bp.id}`, '_blank')}
                      className="w-full text-xs font-semibold bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Details ansehen
                    </button>
                  </div>
                </div>
              </Popup>
            </GeoJSON>
          );
        })}

        {/* Listing markers */}
        {offsetListings.map((listing) => {
          const lKey = `listing-${listing.id}`;
          // show selected icon while animating out (deselectingKey) so the shrink has something to shrink
          const isSelected = selectedKey === lKey || deselectingKey === lKey;
          const isHovered = listing.id === hoveredListingId;
          let listingIcon: L.DivIcon;
          if (listing.address_verified) {
            listingIcon = isSelected && isHovered ? verifiedSelectedHoverIcon
              : isSelected ? verifiedSelectedIcon
              : isHovered ? verifiedHoverIcon
              : verifiedIcon;
          } else {
            listingIcon = isSelected && isHovered ? unverifiedSelectedHoverIcon
              : isSelected ? unverifiedSelectedIcon
              : isHovered ? unverifiedHoverIcon
              : unverifiedIcon;
          }
          return (
          <Marker
            key={lKey}
            ref={(ref) => { markerRefs.current[lKey] = ref; }}
            position={[listing.latitude!, listing.longitude!]}
            icon={listingIcon}
            zIndexOffset={isSelected ? 2000 : isHovered ? 1000 : 0}
            eventHandlers={{
              mouseover: () => onHoverListing?.(listing.id),
              mouseout: () => onHoverListing?.(null),
              click: () => handleSelect(lKey),
            }}
          >
            <Popup className="modern-popup">
              <div className="w-[280px]">
                {/* Status accent bar */}
                <div className={`h-1 ${listing.address_verified ? 'bg-green-500' : 'bg-amber-400'}`} />

                <div className="p-4">
                  {/* Platform + verification */}
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 leading-none">
                      {listing.resolved_platform || listing.platform}
                    </span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium leading-none ring-1 ${
                      listing.address_verified
                        ? 'bg-green-50 text-green-700 ring-green-200'
                        : 'bg-amber-50 text-amber-700 ring-amber-200'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${listing.address_verified ? 'bg-green-500' : 'bg-amber-400'}`} />
                      {listing.address_verified ? 'Verifiziert' : 'Nicht verifiziert'}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="font-semibold text-sm text-gray-900 leading-snug line-clamp-2 mb-2 pr-4">
                    {listing.title}
                  </h3>

                  {/* Address */}
                  {(listing.address || listing.zipcode) && (
                    <div className="flex items-start gap-1.5 text-xs text-gray-500 mb-3">
                      <svg className="w-3.5 h-3.5 mt-px shrink-0 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                      <span>{listing.address}{listing.zipcode && `, ${listing.zipcode}`}</span>
                    </div>
                  )}

                  {/* Key facts */}
                  {(listing.listing_type || listing.size || listing.price_per_night) && (
                    <div className="flex items-center flex-wrap gap-1.5 mb-4">
                      {listing.listing_type && (
                        <span className="text-[11px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-md">{listing.listing_type}</span>
                      )}
                      {listing.size && (
                        <span className="text-[11px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-md">{listing.size}</span>
                      )}
                      {listing.price_per_night && (
                        <span className="text-[11px] bg-gray-100 text-gray-700 font-semibold px-2 py-0.5 rounded-md">
                          {listing.price_per_night} {listing.currency}/Nacht
                        </span>
                      )}
                    </div>
                  )}

                  {/* Divider */}
                  <div className="border-t border-gray-100 mb-3" />

                  {/* Actions */}
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => window.open(`/inserat/${listing.id}`, '_blank')}
                      className="w-full text-xs font-semibold bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Details ansehen
                    </button>
                    <button
                      onClick={() => window.open(listing.resolved_url || listing.listing_url, '_blank')}
                      className="w-full text-xs font-medium text-gray-600 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors flex items-center justify-center gap-1.5"
                    >
                      <svg className="w-3.5 h-3.5 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      Auf {listing.resolved_platform || listing.platform} ansehen
                    </button>
                  </div>
                </div>
              </div>
            </Popup>
          </Marker>
          );
        })}

        {/* Generic entity markers */}
        {validMarkers.map((marker) => {
          const mKey = `marker-${marker.color}-${marker.id}`;
          const isSelected = selectedKey === mKey || deselectingKey === mKey;
          return (
          <Marker
            key={mKey}
            ref={(ref) => { markerRefs.current[mKey] = ref; }}
            position={[marker.latitude, marker.longitude]}
            icon={isSelected ? genericSelectedIcons[marker.color] : genericIcons[marker.color]}
            zIndexOffset={isSelected ? 2000 : 0}
            eventHandlers={{ click: () => handleSelect(mKey) }}
          >
            <Popup className="modern-popup">
              <div className="min-w-[200px] max-w-[260px] p-3">
                <h3 className="font-semibold text-sm text-gray-900 leading-tight mb-1">{marker.label}</h3>
                {marker.sublabel && (
                  <p className="text-xs text-gray-500 mb-2">{marker.sublabel}</p>
                )}
                <button
                  onClick={() => window.open(marker.link, '_blank')}
                  className="w-full text-xs font-medium bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 transition-colors"
                >
                  {marker.linkLabel || 'Details ansehen'}
                </button>
              </div>
            </Popup>
          </Marker>
          );
        })}
      </MapContainer>

      {/* Top-right control cluster: selection-mode toggle (+ help) and satellite toggle */}
      <div className="absolute top-3 right-3 z-[500] flex items-center gap-2">
        {onToggleSelectionMode && (
          <>
            <span
              className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white text-gray-400 border border-gray-200 shadow-sm text-[11px] font-semibold cursor-help select-none"
              title="Mehrfachauswahl: Shift+Ziehen zeichnet ein Rechteck und markiert alle Inserate darin. Alternativ: Auswahl-Modus aktivieren und ohne Shift ziehen. Ctrl/Cmd+Ziehen erweitert eine bestehende Auswahl."
              aria-label="Hilfe zur Mehrfachauswahl"
            >
              ?
            </span>
            <button
              onClick={onToggleSelectionMode}
              title="Auswahl-Modus an/aus"
              aria-pressed={!!selectionMode}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold shadow-md border transition-colors ${
                selectionMode
                  ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                  : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V6a2 2 0 012-2h2M4 16v2a2 2 0 002 2h2M20 8V6a2 2 0 00-2-2h-2M20 16v2a2 2 0 01-2 2h-2" />
              </svg>
              Auswahl
            </button>
          </>
        )}

        {/* Bebauungsplan layer toggle */}
        <button
          onClick={() => setShowBebauungsplaene(s => !s)}
          title="Bebauungspläne als Polygone einblenden"
          aria-pressed={showBebauungsplaene}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold shadow-md border transition-colors ${
            showBebauungsplaene
              ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
              : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'
          }`}
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4h6v6H4V4zm10 0h6v6h-6V4zM4 14h6v6H4v-6zm10 0h6v6h-6v-6z" />
          </svg>
          B-Pläne
        </button>

        {/* Satellite toggle button */}
        <button
          onClick={() => setSatellite(s => !s)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold shadow-md border transition-colors ${
            satellite
              ? 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'
              : 'bg-gray-900 text-white border-gray-900 hover:bg-gray-700'
          }`}
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" />
          </svg>
          {satellite ? 'Karte' : 'Satellit'}
        </button>
      </div>
    </div>
  );
};

export default Map;
